import { type QuestionResult, SCALE_PRESETS, scaleAverage } from '@floorpoll/shared';
import { useCountUp } from '../lib/useCountUp.js';

function AnimatedCount({ value }: { value: number }) {
  return <>{useCountUp(value)}</>;
}

interface Props {
  questions: QuestionResult[];
  /** Big-screen mode scales type up for reading across a room. */
  large?: boolean;
  /** Counts ease toward new values as votes land. Live views only. */
  animated?: boolean;
}

/**
 * Shared between the console results view and the big screen so both always
 * present the same numbers the same way.
 */
export function ResultBars({ questions, large = false, animated = false }: Props) {
  return (
    <div className={large ? 'results results-large' : 'results'}>
      {questions.map((question) => {
        // The current front-runner gets the full-strength bar; the rest step
        // back slightly, so the room can read the standings at a glance.
        const leadCount = Math.max(...question.options.map((option) => option.count), 0);

        // #50 — the bars (columns, on the big screen) are already a rating's
        // distribution; the mean is the statistic it is asked for and no count
        // can show. Null before anyone has rated, so the line reads "0 votes"
        // rather than averaging nothing.
        const average = question.scale
          ? scaleAverage(
              question.options.map((option) => option.count),
              question.scale,
            )
          : null;

        return (
          <div className="result-question" key={question.questionId}>
            <h3>{question.text}</h3>
            {/* #75 — this component is the only thing the big screen renders
              question content through, so the projector's copy of the image
              arrives here rather than off `state.screen`. */}
            {question.imageUrl ? (
              <img className="result-question-image" src={question.imageUrl} alt="" />
            ) : null}
            {/* Percentages are shares of the people who answered, so a
                multi-select question's sum past 100. Saying "voters · multiple
                picks" is what stops that reading as a broken projector from the
                back of the room. */}
            <p className="muted result-total">
              {animated ? <AnimatedCount value={question.totalVotes} /> : question.totalVotes}{' '}
              {question.multiSelect
                ? `${question.totalVotes === 1 ? 'voter' : 'voters'} · multiple picks`
                : question.totalVotes === 1
                  ? 'vote'
                  : 'votes'}
              {average === null ? null : ` · average ${average.toFixed(1)}`}
            </p>

            {large && question.scale ? (
              <ScaleColumns question={question} leadCount={leadCount} animated={animated} />
            ) : (
              <ul className="result-list">
                {question.options.map((option) => {
                  const isLeader = leadCount > 0 && option.count === leadCount;
                  return (
                    <li key={option.optionId} className={isLeader ? 'result-leader' : undefined}>
                      <div className="result-label">
                        {option.imageUrl ? (
                          <img className="result-option-image" src={option.imageUrl} alt="" />
                        ) : null}
                        <span className="result-text">{option.text}</span>
                        <span className="result-count">
                          {animated ? <AnimatedCount value={option.count} /> : option.count} ·{' '}
                          {option.percentage}%
                        </span>
                      </div>
                      <div
                        className="result-bar"
                        role="meter"
                        aria-valuenow={option.percentage}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={
                          question.multiSelect
                            ? `${option.text}: picked by ${option.count} of ${question.totalVotes} voters, ${option.percentage} percent`
                            : `${option.text}: ${option.count} votes, ${option.percentage} percent`
                        }
                      >
                        {/* Width transitions so a vote arriving reads as movement. */}
                        <span style={{ width: `${option.percentage}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A rating on the projector (#50): a histogram, the points along the floor.
 *
 * Bars cost a full row per point, so a 0–10 scale was eleven rows — taller than
 * a 1080p projector before anything else was on it. Columns cost one row
 * whatever the scale's length, and a distribution is read as a shape anyway:
 * where the room clusters, and how far the tail runs.
 *
 * Big screen only. The console keeps bars, where a panel scrolls and exact
 * numbers per point are the point. Heights are relative to the front-runner,
 * so the tallest column always fills the chart and eleven small shares do not
 * sit in its bottom quarter; the share itself stays in the meter's label, in
 * the same words a bar uses, and on the column when there is room to print it.
 *
 * The preset's anchors print under the ends, as they do under the phone's
 * buttons, because a 7 means nothing until the room knows which end is good.
 */
function ScaleColumns({
  question,
  leadCount,
  animated,
}: {
  question: QuestionResult;
  leadCount: number;
  animated: boolean;
}) {
  const shape = question.scale ? SCALE_PRESETS[question.scale] : null;

  return (
    <div className="scale-chart">
      <ol className="scale-columns">
        {question.options.map((option) => {
          const isLeader = leadCount > 0 && option.count === leadCount;
          const height = leadCount > 0 ? (option.count / leadCount) * 100 : 0;
          return (
            <li key={option.optionId} className={isLeader ? 'result-leader' : undefined}>
              <span className="scale-count">
                {animated ? <AnimatedCount value={option.count} /> : option.count}
                <span className="scale-share">{option.percentage}%</span>
              </span>
              <div
                className="scale-column"
                role="meter"
                aria-valuenow={option.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${option.text}: ${option.count} votes, ${option.percentage} percent`}
              >
                {/* Height transitions so a vote arriving reads as movement. */}
                <span style={{ height: `${height}%` }} />
              </div>
              <span className="scale-point">{option.text}</span>
            </li>
          );
        })}
      </ol>
      {shape ? (
        <p className="scale-anchors" aria-hidden="true">
          <span>{shape.minLabel}</span>
          <span>{shape.maxLabel}</span>
        </p>
      ) : null}
    </div>
  );
}
